import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Camera, Users, Plus, Edit, Save, Video, HardDrive, Cloud, Trash2, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const Settings = () => {
  const { api, user, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('webcam');

  // Webcam settings
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [previewStream, setPreviewStream] = useState(null);
  const videoRef = useRef(null);

  // Retention settings
  const [retentionDays, setRetentionDays] = useState(30);
  const [autoDeleteEnabled, setAutoDeleteEnabled] = useState(false);

  // Cloud upload settings
  const [cloudUploadEnabled, setCloudUploadEnabled] = useState(false);
  const [uploadStartHour, setUploadStartHour] = useState('22');
  const [uploadEndHour, setUploadEndHour] = useState('6');
  const [azureConfigured, setAzureConfigured] = useState(false);
  const [testingAzure, setTestingAzure] = useState(false);
  const [azureTestResult, setAzureTestResult] = useState(null);

  const [savingSettings, setSavingSettings] = useState(false);

  // User management
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userModal, setUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'executive' });

  useEffect(() => {
    loadDevices();
    if (isAdmin) {
      loadSettings();
      loadUsers();
    }

    return () => {
      if (previewStream) {
        previewStream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setDevices(videoDevices);

      const savedDevice = localStorage.getItem('snd_vms_camera');
      if (savedDevice && videoDevices.find(d => d.deviceId === savedDevice)) {
        setSelectedDevice(savedDevice);
      } else if (videoDevices.length > 0) {
        setSelectedDevice(videoDevices[0].deviceId);
      }
    } catch (err) {
      console.error('Failed to enumerate devices:', err);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await api.get('/settings');
      setRetentionDays(res.data.settings.retentionDays || 30);
      setAutoDeleteEnabled(res.data.settings.autoDeleteEnabled || false);
      setCloudUploadEnabled(res.data.settings.cloudUploadEnabled || false);
      setUploadStartHour(String(res.data.settings.uploadStartHour || 22));
      setUploadEndHour(String(res.data.settings.uploadEndHour || 6));
      setAzureConfigured(res.data.settings.azureConfigured || false);
    } catch (err) {
      console.error('Failed to load settings:', err);
    }
  };

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const res = await api.get('/users');
      setUsers(res.data.users);
    } catch (err) {
      toast.error('Failed to load users');
    }
    setLoadingUsers(false);
  };

  const testCamera = async () => {
    if (previewStream) {
      previewStream.getTracks().forEach(track => track.stop());
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: selectedDevice ? { deviceId: { exact: selectedDevice } } : true
      });
      setPreviewStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      toast.success('Camera connected successfully');
    } catch (err) {
      toast.error('Failed to access camera');
    }
  };

  const saveCamera = () => {
    localStorage.setItem('snd_vms_camera', selectedDevice);
    toast.success('Camera preference saved');
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put('/settings', {
        retentionDays,
        autoDeleteEnabled,
        cloudUploadEnabled,
        uploadStartHour: parseInt(uploadStartHour),
        uploadEndHour: parseInt(uploadEndHour)
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
    setSavingSettings(false);
  };

  const testAzureConnection = async () => {
    setTestingAzure(true);
    setAzureTestResult(null);
    try {
      const res = await api.post('/settings/test-azure');
      setAzureTestResult(res.data);
      if (res.data.success) {
        toast.success('Azure connection successful!');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      setAzureTestResult({ success: false, message: err.message });
      toast.error('Failed to test Azure connection');
    }
    setTestingAzure(false);
  };

  const openUserModal = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setUserForm({
        name: userToEdit.name,
        email: userToEdit.email,
        password: '',
        role: userToEdit.role
      });
    } else {
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'executive' });
    }
    setUserModal(true);
  };

  const saveUser = async () => {
    try {
      if (editingUser) {
        const updateData = { ...userForm };
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated');
      } else {
        await api.post('/users', userForm);
        toast.success('User created');
      }
      setUserModal(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save user');
    }
  };

  const toggleUserActive = async (userToToggle) => {
    try {
      await api.put(`/users/${userToToggle.id}`, { ...userToToggle, active: !userToToggle.active });
      toast.success(userToToggle.active ? 'User deactivated' : 'User activated');
      loadUsers();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const roleBadge = (role) => {
    const styles = {
      admin: 'bg-red-500/20 text-red-400 border-red-500/30',
      manager: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      executive: 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-md border capitalize ${styles[role]}`}>
        {role}
      </span>
    );
  };

  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: String(i),
    label: `${i === 0 ? '12' : i > 12 ? i - 12 : i}:00 ${i < 12 ? 'AM' : 'PM'}`
  }));

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Settings</h1>
          <p className="text-zinc-400 mt-1">Configure system preferences</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
          <TabsTrigger value="webcam" className="data-[state=active]:bg-zinc-800" data-testid="tab-webcam">
            <Camera className="w-4 h-4 mr-2" />
            Webcam
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="retention" className="data-[state=active]:bg-zinc-800" data-testid="tab-retention">
                <HardDrive className="w-4 h-4 mr-2" />
                Retention
              </TabsTrigger>
              <TabsTrigger value="cloud" className="data-[state=active]:bg-zinc-800" data-testid="tab-cloud">
                <Cloud className="w-4 h-4 mr-2" />
                Cloud Upload
              </TabsTrigger>
              <TabsTrigger value="users" className="data-[state=active]:bg-zinc-800" data-testid="tab-users">
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Webcam Tab */}
        <TabsContent value="webcam" className="mt-6">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="border-b border-zinc-800 pb-4">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Camera className="w-5 h-5 text-indigo-400" />
                Webcam Configuration
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Select and test your camera for recording
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-zinc-300">Select Camera</Label>
                    <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                      <SelectTrigger className="mt-2 bg-zinc-950 border-zinc-800" data-testid="camera-select">
                        <SelectValue placeholder="Choose a camera" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {devices
                          .filter(device => device.deviceId)
                          .map(device => (
                            <SelectItem key={device.deviceId} value={device.deviceId}>
                              {device.label || `Camera ${devices.indexOf(device) + 1}`}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={testCamera} className="bg-indigo-600 hover:bg-indigo-500" data-testid="test-camera-btn">
                      <Video className="w-4 h-4 mr-2" />
                      Test Camera
                    </Button>
                    <Button onClick={saveCamera} variant="outline" className="border-zinc-700" data-testid="save-camera-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Save Preference
                    </Button>
                  </div>
                </div>

                <div className="aspect-video bg-zinc-950 rounded-lg overflow-hidden border border-zinc-800">
                  {previewStream ? (
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                      data-testid="camera-preview"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <Camera className="w-12 h-12" />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Retention Tab */}
        {isAdmin && (
          <TabsContent value="retention" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="border-b border-zinc-800 pb-4">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <HardDrive className="w-5 h-5 text-indigo-400" />
                  Video Retention Settings
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Configure automatic video deletion
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div className="flex items-center justify-between p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <div>
                    <p className="text-zinc-300 font-medium">Auto Delete Videos</p>
                    <p className="text-sm text-zinc-500">Automatically delete videos older than retention period</p>
                  </div>
                  <Switch
                    checked={autoDeleteEnabled}
                    onCheckedChange={setAutoDeleteEnabled}
                    data-testid="auto-delete-switch"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Retention Period (Days)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(parseInt(e.target.value) || 30)}
                    className="max-w-xs bg-zinc-950 border-zinc-800"
                    data-testid="retention-days-input"
                  />
                  <p className="text-sm text-zinc-500">
                    Videos older than {retentionDays} days will be {autoDeleteEnabled ? 'automatically deleted' : 'marked for deletion when enabled'}
                  </p>
                </div>

                <Button onClick={saveSettings} disabled={savingSettings} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-retention-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Cloud Upload Tab */}
        {isAdmin && (
          <TabsContent value="cloud" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="border-b border-zinc-800 pb-4">
                <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-indigo-400" />
                  Cloud Upload Settings
                </CardTitle>
                <CardDescription className="text-zinc-400">
                  Configure background upload to Azure Blob Storage during off-peak hours
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Azure Connection Status */}
                <div className={`p-4 rounded-lg border ${azureConfigured ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {azureConfigured ? (
                        <CheckCircle2 className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-yellow-400" />
                      )}
                      <div>
                        <p className={`font-medium ${azureConfigured ? 'text-green-300' : 'text-yellow-300'}`}>
                          {azureConfigured ? 'Azure Storage Connected' : 'Azure Storage Not Configured'}
                        </p>
                        <p className="text-sm text-zinc-400">
                          {azureConfigured
                            ? 'Videos will be uploaded to your Azure Blob Storage'
                            : 'Add AZURE_STORAGE_CONNECTION_STRING to backend .env file'}
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={testAzureConnection}
                      disabled={testingAzure}
                      variant="outline"
                      size="sm"
                      className="border-zinc-700"
                      data-testid="test-azure-btn"
                    >
                      {testingAzure ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Cloud className="w-4 h-4 mr-2" />
                      )}
                      Test Connection
                    </Button>
                  </div>
                  {azureTestResult && (
                    <div className={`mt-3 p-3 rounded ${azureTestResult.success ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      <p className={`text-sm ${azureTestResult.success ? 'text-green-300' : 'text-red-300'}`}>
                        {azureTestResult.message}
                        {azureTestResult.container && ` (Container: ${azureTestResult.container})`}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <div>
                    <p className="text-zinc-300 font-medium">Enable Cloud Upload</p>
                    <p className="text-sm text-zinc-500">Upload compressed videos to Azure during off-peak hours</p>
                  </div>
                  <Switch
                    checked={cloudUploadEnabled}
                    onCheckedChange={setCloudUploadEnabled}
                    data-testid="cloud-upload-switch"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 max-w-md">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Upload Start Time</Label>
                    <Select value={uploadStartHour} onValueChange={setUploadStartHour}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="upload-start-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                        {hourOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-zinc-300">Upload End Time</Label>
                    <Select value={uploadEndHour} onValueChange={setUploadEndHour}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="upload-end-hour">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                        {hourOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
                  <p className="text-sm text-indigo-300">
                    <strong>Note:</strong> Videos will be uploaded to Azure Blob Storage between{' '}
                    {hourOptions.find(h => h.value === uploadStartHour)?.label} and{' '}
                    {hourOptions.find(h => h.value === uploadEndHour)?.label}.
                    This ensures uploads don't interfere with packing operations during busy hours.
                  </p>
                </div>

                <div className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                  <p className="text-sm text-zinc-400 mb-2">
                    <strong className="text-zinc-300">Video Compression:</strong> Videos are automatically compressed using H.264 codec after recording.
                    This typically reduces file size by 30-50% while maintaining good quality.
                  </p>
                  <p className="text-sm text-zinc-400">
                    <strong className="text-zinc-300">Output Format:</strong> MP4 (720p, ~2.5 Mbps)
                  </p>
                </div>

                <Button onClick={saveSettings} disabled={savingSettings} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-cloud-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Users Tab */}
        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="border-b border-zinc-800 pb-4 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-400" />
                    User Management
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Manage system users and roles
                  </CardDescription>
                </div>
                <Button onClick={() => openUserModal()} className="bg-indigo-600 hover:bg-indigo-500" data-testid="add-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">Name</TableHead>
                      <TableHead className="text-zinc-400">Email</TableHead>
                      <TableHead className="text-zinc-400">Role</TableHead>
                      <TableHead className="text-zinc-400">Status</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingUsers ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8">
                          <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                          No users found
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((u) => (
                        <TableRow key={u.id} className="border-zinc-800" data-testid={`user-row-${u.id}`}>
                          <TableCell className="text-zinc-300 font-medium">{u.name}</TableCell>
                          <TableCell className="text-zinc-400">{u.email}</TableCell>
                          <TableCell>{roleBadge(u.role)}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 text-xs rounded-md border ${u.active
                                ? 'bg-green-500/20 text-green-400 border-green-500/30'
                                : 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
                              }`}>
                              {u.active ? 'Active' : 'Inactive'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openUserModal(u)}
                                className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
                                data-testid={`edit-user-${u.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => toggleUserActive(u)}
                                className={`h-8 w-8 p-0 ${u.active ? 'text-red-400 hover:text-red-300' : 'text-green-400 hover:text-green-300'}`}
                                data-testid={`toggle-user-${u.id}`}
                              >
                                {u.active ? <Trash2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* User Modal */}
      <Dialog open={userModal} onOpenChange={setUserModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800" data-testid="user-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Email</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800"
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">{editingUser ? 'New Password (leave blank to keep)' : 'Password'}</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                className="mt-1 bg-zinc-950 border-zinc-800"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Role</Label>
              <Select value={userForm.role} onValueChange={(val) => setUserForm({ ...userForm, role: val })}>
                <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="executive">Executive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setUserModal(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={saveUser} className="bg-indigo-600 hover:bg-indigo-500" data-testid="save-user-btn">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Settings;
